import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { markPosted } from '../lib/feed-flash';
import { Darkroom, PrintOnPeg, PrintReveal, PrintTeaser, type PrintsLayout } from '../components/Reveal';
import { StoryOpen } from '../components/StoryPrint';
import { SendStory } from '../components/SendStory';
import type { Storyboard } from '../lib/storyboard';
import fixture from '../lib/storyboard.fixture.json';
import { CastAsk, CastExtras, CastTag, ThisIsYou } from '../components/Cast';

/** A Story About You Two, opened: the sample picture stands in for the crew's four-panel page */
const FRIEND_SB = { ...fixture, storyId: 'dev', day: 4, challengeId: 'story-about-a-friend', status: 'done', source: 'audio', title: 'The Night of the Wrong Train', pageUrl: '/art/reveal/sample-pudding.jpg' } as unknown as Storyboard;

/** Be in your story (well/product/32): Day 1's story before and after it is recast; stand-in pictures, the real layout */
const PLUSH_SB = { ...fixture, storyId: 'dev-cast', day: 1, challengeId: 'warmup', status: 'done', source: 'warmup', title: 'The Car Park', pageUrl: '/art/reveal/sample-pudding.jpg', castable: true } as unknown as Storyboard;
const CAST_SB = { ...PLUSH_SB, castable: false, pageUrl: '/art/exercises/storyboard-a-memory.jpg', beforePageUrl: PLUSH_SB.pageUrl, cast: { status: 'cast', name: 'Sam', meUrl: '/art/crew/the-moon.jpg', canRedraw: true } } as unknown as Storyboard;

/**
 * DEV only: every scene of the wait and the reveal on one url, for design
 * review without running an exercise. /dev/reveal?scene=peg&count=3&layout=line
 */
const SCENES = ['darkroom', 'lifting', 'peg', 'open', 'teaser', 'friend', 'ask', 'tag', 'recast', 'gold', 'cast', 'you'] as const;
const LAYOUTS: PrintsLayout[] = ['deck', 'line', 'tally'];
const WORDS = 'A grocery store where every shelf is pudding';
/** a crew picture of those words, as a real one looks: full bleed, nothing but the drawing */
const PICTURE = '/art/reveal/sample-pudding.jpg';

export function DevReveal() {
  const [q, setQ] = useSearchParams();
  const scene = (q.get('scene') || 'peg') as (typeof SCENES)[number];
  const count = Number(q.get('count') || 1);
  const layout = (q.get('layout') || 'deck') as PrintsLayout;
  const key = `${scene}-${count}-${layout}-${q.get('n') || 0}`;
  const set = (k: string, v: string) => { const n = new URLSearchParams(q); n.set(k, v); n.set('n', String(Number(q.get('n') || 0) + 1)); setQ(n); };
  const noop = () => {};
  const nav = useNavigate();
  // the open is full bleed, so the switcher folds away there until asked for
  const [bar, setBar] = useState(scene !== 'open' && scene !== 'friend' && scene !== 'ask' && scene !== 'cast');

  return (
    <>
      {scene === 'darkroom' && <Darkroom key={key} day={3} words={WORDS} onLeave={noop} />}
      {scene === 'lifting' && <Darkroom key={key} day={3} words={WORDS} lifting onLeave={noop} />}
      {scene === 'peg' && <PrintOnPeg key={key} day={3} words={WORDS} count={count} layout={layout} onTurn={() => set('scene', 'open')} />}
      {scene === 'open' && <PrintReveal key={key} day={3} words={WORDS} url={PICTURE} onPost={() => markPosted(PICTURE)} onDone={() => nav('/path', { state: { landed: 'ten-bad-ideas' } })} />}
      {scene === 'friend' && <StoryOpen key={key} sb={FRIEND_SB} onDone={noop} kicker="Storyboarded by the Moon · you and Alan" extra={<SendStory friend="Alan" />} />}
      {scene === 'ask' && <StoryOpen key={key} sb={PLUSH_SB} onDone={noop} ask={<CastAsk onYes={() => set('scene', 'tag')} onNo={noop} />} />}
      {scene === 'tag' && <CastTag key={key} sb={PLUSH_SB} onBack={() => set('scene', 'ask')} onSent={() => set('scene', 'recast')} />}
      {scene === 'recast' && <Darkroom key={key} day={1} words="Starring Sam" says="Redrawing it with you in it" onLeave={noop} />}
      {scene === 'gold' && <PrintOnPeg key={key} day={1} words="Starring Sam" gold line="You’re in it, Sam." onTurn={() => set('scene', 'cast')} />}
      {scene === 'cast' && <StoryOpen key={key} sb={CAST_SB} onDone={() => set('scene', 'you')} wipeFrom={PLUSH_SB.pageUrl} kicker="Storyboarded by the Moon · starring Sam" extra={<CastExtras sb={CAST_SB} onRedraw={noop} err={null} />} />}
      {scene === 'you' && <ThisIsYou key={key} meUrl="/art/crew/the-moon.jpg" onDone={noop} />}
      {scene === 'teaser' && <div className="phone" style={{ padding: 16 }}><PrintTeaser key={key} day={3} words={WORDS} count={count} onTurn={noop} /></div>}
      {!bar && <button type="button" className="dev-fold" onClick={() => setBar(true)}>dev</button>}
      {bar && <div className="dev-bar">
        <button onClick={() => setBar(false)}>×</button>
        {SCENES.map((s) => <button key={s} className={s === scene ? 'on' : ''} onClick={() => set('scene', s)}>{s}</button>)}
        <span>·</span>
        {[1, 2, 3, 5].map((c) => <button key={c} className={c === count ? 'on' : ''} onClick={() => set('count', String(c))}>{c}</button>)}
        <span>·</span>
        {LAYOUTS.map((l) => <button key={l} className={l === layout ? 'on' : ''} onClick={() => set('layout', l)}>{l}</button>)}
      </div>}
    </>
  );
}
